import { APIRequest } from '@lynx/client-core/src/api'
import { LoginModal } from '@lynx/client-core/src/components'
import { Logger } from '@lynx/client-core/src/modules'
import { useThunkDispatch } from 'hooks'
import i18next from 'i18next'
import { makeVNCConnection } from 'modules/Vnc'
import * as qs from 'query-string'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ApplicationState } from 'store'
import { endConnection, internalSubscribe, makeReadOnly, setConnected } from 'store/session'
import { Ended, Qr, Started, Starting } from './components'
import css from './HomePage.module.scss'

export const HomePage = (): React.ReactElement => {
	const dispatchThunk = useThunkDispatch()
	const dispatch = useDispatch()
	const {
		config: { MESSAGING_WS_SERVER, LYNXCLOUD_CLIENT, ENVIRONMENT },
		profile: { displayName },
		session: { channelId, username, password, isAdmin, ended, url, qr, percent, status }
	} = useSelector((state: ApplicationState) => state)
	const [showQr, setShowQr] = useState<boolean>(false)
	const [profileInput, setProfileInput] = useState<string>('')
	const [profanity, setProfanity] = useState<string[]>([])
	const [query] = useState<qs.ParsedQuery<string>>(qs.parse(location.search))
	const [isError, setISError] = useState<boolean>(false)

	useEffect((): void => {
		const mount = async (): Promise<void> => {
			const profanityOutput = await APIRequest.Profanity.list()
			setProfanity(profanityOutput)
		}

		mount()
	}, [])

	useEffect(() => {
		const setup = async (): Promise<void> => {
			if (!query) {
				return
			}

			const { name, userId, channelId: channelIdQuery, accessToken, displayName: displayNameQuery } = query
			const actualDisplayName = displayName || `${displayNameQuery}`

			setProfileInput(actualDisplayName)
			setProfanity(profanity)

			if (!name && !userId) {
				if (channelIdQuery) {
					dispatch(makeReadOnly(`${accessToken}`, `${channelIdQuery}`, actualDisplayName))
					return
				} else {
					setISError(true)
					return
				}
			}

			await adminSetup()
		}

		setup()
	}, [query])

	useEffect(() => {
		const sendJoin = async (): Promise<void> => {
			const { displayName: displayNameQuery, channelId: channelIdQuery } = query
			if (channelIdQuery) {
				const actualDisplayName = displayName || `${displayNameQuery}`
				await APIRequest.Channels.sendMessage(`${channelIdQuery}`, 'vnc:joined', { name: actualDisplayName, nameUpdate: false })
			}
		}
		sendJoin()
	}, [username])

	useEffect(() => {
		const connect = async (): Promise<void> => {
			if (!username || !password) {
				return
			}
			const isConnected = await makeVNCConnection(isAdmin, username, password)
			dispatch(setConnected(isConnected))
		}
		connect()
	}, [username])

	useEffect(() => {
		const cleanUp = async (): Promise<void> => {
			if (channelId && isAdmin) {
				window.addEventListener('beforeunload', async (ev) => {
					ev.preventDefault()
					//Drop VNC connection
					dispatch(endConnection())
					//Let other connections know this is happening
					await APIRequest.Channels.sendMessage(channelId, 'vnc:close', {})
					//Delete channel
					await APIRequest.Channels.delete(channelId)
				})
			}
		}
		cleanUp()
	}, [channelId])

	const adminSetup = async (): Promise<void> => {
		const { file, name, userId, provider, paths, shareId, shareOwnerId } = query
		try {
			if (username && password) {
				return
			}
			await dispatchThunk(
				internalSubscribe(
					`${file}`,
					`${name}`,
					`${userId}`,
					`${provider}`,
					`${paths}`,
					`${shareId}`,
					`${shareOwnerId}`,
					LYNXCLOUD_CLIENT,
					MESSAGING_WS_SERVER,
					ENVIRONMENT,
					MESSAGING_WS_SERVER
				)
			)
		} catch (err) {
			Logger.log(err)
		}
	}

	const setQrInternal = (): void => {
		setShowQr(!showQr)
	}

	if (isError) {
		return <h1>{i18next.t('pages.litb.somethingWrong')}</h1>
	}

	return (
		<div className={css['container']}>
			<div className={css['body']}>
				<Qr url={url} qr={qr} showQr={showQr} setShowQr={setQrInternal} />
				<Started setShowQr={setQrInternal} accessToken={query.accessToken as string} profanity={profanity} profileInput={profileInput} />
				<Starting connected={!!username} percent={percent} status={status} />
				<Ended ended={ended} />
			</div>
			<LoginModal />
		</div>
	)
}
